<template>
  <section class="flight-ticket-preview">
    <div class="flight-ticket-preview__document">
      <document-preview :file="file" />
    </div>
    <aside class="flight-ticket-preview__analysis">
      <header>
        <div><span>AI recognition</span><strong>{{ segmentSummary }}</strong></div>
        <app-status-badge design="new" :label="statusName" :color="statusColor" compact />
      </header>
      <p class="flight-ticket-preview__provider">{{ providerSummary }}</p>
      <div v-if="segments.length" class="flight-ticket-preview__segments">
        <button
          v-for="segment in segments"
          :key="segment.id"
          type="button"
          :class="{ 'flight-ticket-preview__segment--active': previewSegment && previewSegment.id === segment.id }"
          @click="$emit('select-segment', segment.id)"
        >
          <strong>{{ segment.flight_number || 'Unknown flight' }}</strong>
          <span>{{ segment.departure_airport_iata || 'Not set' }} → {{ segment.arrival_airport_iata || 'Not set' }}</span>
        </button>
      </div>
      <dl v-if="previewSegment" class="flight-ticket-preview__data">
        <div><dt>Route</dt><dd>{{ previewSegment.departure_airport_iata || 'Not set' }} → {{ previewSegment.arrival_airport_iata || 'Not set' }}</dd></div>
        <div><dt>Flight</dt><dd>{{ previewSegment.flight_number || 'Not set' }}</dd></div>
        <div><dt>Departure</dt><dd>{{ previewValue('departure') }}</dd></div>
        <div><dt>Arrival</dt><dd>{{ previewValue('arrival') }}</dd></div>
        <div><dt>Airline</dt><dd>{{ previewSegment.airline_name || 'Not set' }}</dd></div>
      </dl>
      <section v-if="warnings.length" class="flight-ticket-preview__warnings">
        <strong>{{ warnings.length }} recognition {{ warnings.length === 1 ? 'warning' : 'warnings' }}</strong>
        <ul><li v-for="warning in warnings" :key="warning">{{ warning }}</li></ul>
      </section>
    </aside>
  </section>
</template>

<script>
import DocumentPreview from './DocumentPreview'

export default {
  name: 'FlightTicketPreviewPanel',
  components: { DocumentPreview },
  props: {
    file: { type: Object, required: true },
    segments: { type: Array, default: () => [] },
    warnings: { type: Array, default: () => [] },
    selectedSegmentId: { type: Number, default: null },
    fallbackSegment: { type: Object, default: null },
    providerSummary: { type: String, required: true },
    statusName: { type: String, required: true },
    statusColor: { type: String, default: null }
  },
  computed: {
    previewSegment() {
      return this.segments.find(segment => segment.id === this.selectedSegmentId)
        || this.fallbackSegment
        || this.segments[0]
        || null
    },
    segmentSummary() {
      return `${this.segments.length} flight ${this.segments.length === 1 ? 'segment' : 'segments'} found`
    }
  },
  methods: {
    previewValue(direction) {
      const segment = this.previewSegment || {}
      const date = segment[`${direction}_date`] || 'Date not set'
      const rawTime = segment[`${direction}_time`]
      const time = rawTime ? String(rawTime).slice(0, 5) : 'Time not set'
      const airport = segment[`${direction}_airport_iata`] || 'Airport not set'
      return `${date} · ${time} · ${airport}`
    }
  }
}
</script>

<style lang="sass" scoped>
.flight-ticket-preview
  display: grid
  height: 100%
  min-height: 0
  grid-template-columns: minmax(0, 7fr) minmax(280px, 3fr)
  gap: 14px

.flight-ticket-preview__document
  display: flex
  min-height: 0
  overflow: hidden
  > *
    flex: 1

.flight-ticket-preview__analysis
  min-height: 0
  padding: 16px
  border: 1px solid $ui-line
  border-radius: 12px
  overflow-y: auto
  background: $ui-surface
  > header
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 10px
    span,
    strong
      display: block
    span
      margin-bottom: 3px
      color: $ui-blue
      font-size: 10px
      font-weight: 700
      text-transform: uppercase
    strong
      color: $ui-ink
      font-size: 15px

.flight-ticket-preview__provider
  margin: 4px 0 14px
  color: $ui-muted
  font-size: 12px

.flight-ticket-preview__segments
  display: grid
  gap: 6px
  margin-bottom: 14px
  button
    display: flex
    align-items: center
    justify-content: space-between
    gap: 10px
    padding: 9px 10px
    border: 1px solid $ui-line
    border-radius: 8px
    background: $ui-surface
    color: $ui-text
    text-align: left
    cursor: pointer
    strong
      color: $ui-ink
      font-size: 13px
    span
      color: $ui-muted
      font-size: 12px
  .flight-ticket-preview__segment--active
    border-color: $ui-blue
    background: $ui-blue-soft

.flight-ticket-preview__data
  display: grid
  gap: 0
  margin: 0
  border-top: 1px solid $ui-line
  div
    padding: 10px 0
    border-bottom: 1px solid $ui-line
  dt
    margin-bottom: 2px
    color: $ui-muted
    font-size: 10px
    font-weight: 600
    text-transform: uppercase
  dd
    margin: 0
    color: $ui-ink
    font-size: 13px
    font-weight: 600

.flight-ticket-preview__warnings
  margin-top: 14px
  padding: 10px
  border-radius: 8px
  background: $ui-amber-soft
  color: $ui-amber
  font-size: 12px
  ul
    margin: 7px 0 0
    padding-left: 17px
  li + li
    margin-top: 4px

@media (max-width: 1100px)
  .flight-ticket-preview
    height: auto
    grid-template-columns: 1fr
  .flight-ticket-preview__document
    min-height: 640px
</style>
